import { deepCloneWith, REMOVE } from '@takeshape/util';
import { baseServiceConfigSchema } from "../common/base-service-provider.js";
export const formSchema = {
    type: 'object',
    required: ['title', 'id', 'namespace', 'endpoint', 'companyId', 'recipientId', 'authentication'],
    properties: {
        title: {
            title: 'Name',
            type: 'string',
            default: 'Syndigo'
        },
        id: {
            title: 'Slug',
            type: 'string',
            pattern: '[0-9a-z_-]+',
            default: 'syndigo'
        },
        namespace: {
            title: 'Namespace',
            type: 'string',
            pattern: '^[A-Z][A-Za-z]+$',
            default: 'Syndigo'
        },
        endpoint: {
            title: 'Endpoint',
            type: 'string',
            default: 'https://api.syndigo.com'
        },
        companyId: {
            title: 'Data Owner ID',
            type: 'string'
        },
        recipientId: {
            title: 'Recipient ID',
            type: 'string'
        },
        authentication: {
            title: 'Authentication',
            description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
            type: 'object',
            properties: {
                username: {
                    title: 'Username',
                    type: 'string',
                    '@sensitive': true
                },
                secret: {
                    title: 'Secret',
                    type: 'string',
                    '@sensitive': true
                }
            },
            required: ['username', 'secret']
        }
    }
};
export const formConfig = {
    order: ['title', 'id', 'namespace', 'endpoint', 'companyId', 'recipientId', 'authentication'],
    properties: {
        title: {
            widget: 'singleLineText',
            instructions: 'A human-readable name for this service.'
        },
        id: {
            widget: 'singleLineText',
            instructions: 'A unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
        },
        namespace: {
            widget: 'singleLineText',
            instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
        },
        endpoint: {
            widget: 'singleLineText'
        },
        companyId: {
            widget: 'singleLineText',
            instructions: 'Example: `79194442-9e19-4716-ac4c-dd5065f0da88`'
        },
        recipientId: {
            widget: 'singleLineText',
            instructions: 'Example: `79194442-9e19-4716-ac4c-dd5065f0da88` or an alias `MyRecipient`.'
        },
        authentication: {
            properties: {
                username: {
                    widget: 'sensitiveField',
                    instructions: 'API username from Syndigo > My Account'
                },
                secret: {
                    widget: 'sensitiveField',
                    instructions: 'API secret from Syndigo > My Account'
                }
            },
            order: ['username', 'secret']
        }
    }
};
export const serviceConfigSchema = {
    ...baseServiceConfigSchema,
    properties: {
        ...baseServiceConfigSchema.properties,
        serviceType: {
            enum: ['rest']
        },
        authenticationType: {
            enum: ['custom']
        },
        namespace: {
            type: 'string'
        },
        authentication: {
            type: 'object',
            properties: {
                type: {
                    enum: ['custom']
                },
                username: {
                    type: 'string'
                },
                secret: {
                    type: 'string'
                }
            },
            required: ['type', 'username', 'secret']
        },
        options: {
            type: 'object',
            properties: {
                endpoint: {
                    type: 'string'
                },
                companyId: {
                    type: 'string'
                },
                recipientId: {
                    type: 'string'
                }
            },
            required: ['endpoint', 'companyId', 'recipientId'],
            additionalProperties: true
        }
    },
    required: [...baseServiceConfigSchema.required, 'namespace', 'authentication', 'options']
};
export const syndigoProductSearchQueryArgsSchema = {
    type: 'object',
    properties: {
        from: {
            type: 'integer',
            description: 'Used in combination with takeCount to paginate large numbers of product records. Quantity of records to skip before you start taking products. Whatever value is used for the takeCount, add to skipCount for the subsequent call.'
        },
        limit: {
            type: 'integer',
            description: 'Sets quantity of results to return at one time. Used for parsing large numbers of product records.'
        },
        locale: {
            type: 'string',
            description: 'Stores the value passed in a language specific variant. Facilitates different values in different langugages on the same attribute. Defaults to en-US.'
        },
        orderBy: {
            type: 'string',
            description: 'Sets return results order.'
        },
        desc: {
            type: 'boolean',
            description: 'Determines sort order (ascending or descending).'
        },
        onHold: {
            type: 'boolean',
            description: 'Determines if the search returns OnHold products or not.'
        },
        archived: {
            type: 'boolean',
            description: 'Determines if the search returns Archived products or not. Note, UNSAFE, archived products may not match the current introspection types and could throw errors.'
        },
        attributeFilterOperator: {
            enum: ['AND', 'OR'],
            description: 'Setting the operator when multiple attribute filters are present. Defaults to AND.'
        },
        attributeExistsFilters: {
            type: 'array',
            items: {
                type: 'string'
            },
            description: 'Returns products that has (does not have) desired attribute.'
        },
        attributeFilters: {
            type: 'array',
            items: {
                type: 'string'
            },
            description: 'Searches the value of attribute defined.'
        },
        searchType: {
            enum: ['Contains', 'Prefix', 'Suffix', 'Fuzzy', 'Search'],
            description: 'Part of attributeFilters.'
        },
        dateFilters: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    name: {
                        enum: [
                            'CreatedDate',
                            'RecordDate',
                            'LastModifiedDate',
                            'DiscontinueDate',
                            'ObsolescenceDate',
                            'LastPublishedDate'
                        ]
                    },
                    operator: {
                        enum: ['LessThan', 'LessThanOrEqualTo', 'GreaterThan', 'GreaterThanOrEqualTo']
                    },
                    value: {
                        type: 'string'
                    },
                    includeMissing: {
                        type: 'boolean'
                    }
                }
            },
            description: 'Searches the products using date filters.'
        },
        publicationStatus: {
            enum: [
                'None',
                'NotPublished',
                'Publishing',
                'PendingSupplierAction',
                'PublishedAwaitingSubscription',
                'PendingRecipientAction',
                'PublishedAwaitingResponse',
                'Synchronized',
                'Error',
                'WithdrawCorrection',
                'WithdrawDelete',
                'Rejected'
            ],
            description: 'Only show items with the specified publication status. Defaults to `None`.'
        },
        productIds: {
            type: 'array',
            items: {
                type: 'string'
            },
            description: 'List of products to be included.'
        },
        identifier: {
            type: 'object',
            patternProperties: {
                '.+': {
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                }
            },
            description: 'Identifier name and values to search.'
        }
    }
};
export const syndigoLayerSchema = {
    shapes: {
        '{{namespace}}_ProductSearchQueryArgs': {
            name: '{{namespace}}_ProductSearchQueryArgs',
            id: '{{namespace}}_ProductSearchQueryArgs',
            title: '{{namespace}}_ProductSearchQueryArgs',
            // TODO remove transform if we do https://app.shortcut.com/takeshape/story/14590/allow-patternproperties-in-propertyschema
            schema: deepCloneWith(syndigoProductSearchQueryArgsSchema, (_, key) => {
                if (key === 'patternProperties') {
                    return REMOVE;
                }
            })
        },
        '{{namespace}}_ProductAsset': {
            name: '{{namespace}}_ProductAsset',
            id: '{{namespace}}_ProductAsset',
            title: '{{namespace}}_ProductAsset',
            schema: {
                properties: {
                    name: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:ProductAsset.Name'
                    },
                    url: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:ProductAsset.Url'
                    },
                    format: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:ProductAsset.Format'
                    }
                },
                required: ['name', 'url'],
                type: 'object'
            }
        },
        '{{namespace}}_ProductAssets': {
            name: '{{namespace}}_ProductAssets',
            id: '{{namespace}}_ProductAssets',
            title: '{{namespace}}_ProductAssets',
            schema: {
                properties: {},
                required: [],
                type: 'object'
            }
        },
        '{{namespace}}_ProductAttributes': {
            name: '{{namespace}}_ProductAttributes',
            id: '{{namespace}}_ProductAttributes',
            title: '{{namespace}}_ProductAttributes',
            schema: {
                properties: {},
                required: [],
                type: 'object'
            }
        },
        '{{namespace}}_Product': {
            name: '{{namespace}}_Product',
            id: '{{namespace}}_Product',
            title: '{{namespace}}_Product',
            schema: {
                properties: {
                    id: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:Product.ProductIdentifierValue'
                    },
                    createdAt: {
                        format: 'date-time',
                        type: 'string',
                        '@mapping': '{{serviceId}}:Product.AuditInfo.CreatedDate'
                    },
                    updatedAt: {
                        format: 'date-time',
                        type: 'string',
                        '@mapping': '{{serviceId}}:Product.AuditInfo.LastModifiedDate'
                    },
                    archived: {
                        type: 'boolean',
                        '@mapping': '{{serviceId}}:Product.Archived'
                    },
                    stagingStatus: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:Product.StagingStatus'
                    },
                    targetIndex: {
                        type: 'string',
                        '@mapping': '{{serviceId}}:Product.TargetIndex'
                    },
                    attributes: {
                        type: 'object',
                        '@ref': '{{serviceId}}:ProductAttributes',
                        '@mapping': '{{serviceId}}:Product.Values'
                    },
                    assets: {
                        type: 'object',
                        '@ref': '{{serviceId}}:ProductAssets',
                        '@mapping': '{{serviceId}}:Product.AssetValues'
                    }
                },
                required: ['id', 'createdAt', 'updatedAt', 'archived', 'stagingStatus', 'targetIndex'],
                type: 'object'
            }
        },
        '{{namespace}}_ProductPaginatedList': {
            name: '{{namespace}}_ProductPaginatedList',
            id: '{{namespace}}_ProductPaginatedList',
            title: '{{namespace}}_ProductPaginatedList',
            schema: {
                type: 'object',
                properties: {
                    total: {
                        type: 'integer',
                        description: 'The total number of items this query may return.'
                    },
                    items: {
                        type: 'array',
                        items: {
                            '@ref': '{{serviceId}}:Product'
                        }
                    }
                },
                required: ['total', 'items']
            }
        }
    },
    queries: {
        searchProducts: {
            shape: '{{namespace}}_ProductPaginatedList',
            args: '{{namespace}}_ProductSearchQueryArgs',
            resolver: {
                name: 'syndigo:searchProducts',
                service: '{{serviceId}}'
            },
            description: 'You can search products by using different variance of basic data available to you such as date, IDs, and attributes.'
        }
    },
    mutations: {}
};
